// ApprovedVendorsContainer.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import employerSvc from "../../services/employer/employerSvc";
import { isTrustedParentMessage } from "../../utils/iframeMessaging";
import ApprovedVendors from "./ApprovedVendors";

export default function ApprovedVendorsContainer() {
  const { employerId, planType } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Debug flag - set to true to enable console logs
  const DEBUG_ROUTING = false;

  const debugLog = useCallback((message, data = null) => {
    if (DEBUG_ROUTING) {
      if (data) {
        console.log('ApprovedVendorsContainer:', message, data);
      } else {
        console.log('ApprovedVendorsContainer:', message);
      }
    }
  }, [DEBUG_ROUTING]);

  // New: State to store parent website
  const [parentWebsite, setParentWebsite] = useState(null);

  // Listen for postMessage from parent for website info
  useEffect(() => {
    function handleMessage(event) {
      if (!isTrustedParentMessage(event)) {
        debugLog("Ignoring message from untrusted source:", event.origin);
        return;
      }
      if (event.data && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via postMessage:", event.data.website);
      } else if (event.data && event.data.type === 'WEBSITE_INFO' && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via WEBSITE_INFO:", event.data.website);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [debugLog]);

  // Compute websiteName, prefer parentWebsite if present
  const websiteName = useMemo(() => {
    if (parentWebsite) {
      switch (parentWebsite) {
        case "region10rams.org":
          return "Region 10 RAMS";
        case "calstrs403bcomply.com":
          return "CALSTRS";
        case "tcgservices.com":
          return "TCG Services";
        case "finpath403b.com":
          return "FinPath 403b";
        case "weabenefits.com":
          return "WEA Benefits";
        // Add more mappings as needed
        default:
          return parentWebsite;
      }
    }
    // Default to TCG Services if no parentWebsite provided
    return "TCG Services";
  }, [parentWebsite]);

  // Extract planType from the URL path parameter and decode it
  const planTypeRaw = planType ? decodeURIComponent(planType) : "";

  // Helper function to normalize plan types for comparison
  const normalizePlanType = (planTypeStr) => {
    if (!planTypeStr) return "";
    return planTypeStr
      .replace(/\s/g, "") // Remove all spaces
      .replace(/[()]/g, "") // Remove parentheses
      .toLowerCase(); // Convert to lowercase
  };

  // Local states
  const [loading, setLoading] = useState(true);
  const [approvedVendorsData, setApprovedVendorsData] = useState([]);
  const [employerInfoData, setEmployerInfoData] = useState({
    name: "",
    state: "",
    reliusWeb: "",
    logo: "",
  });

  useEffect(() => {
    // Make API call if we have a parentWebsite OR if we're in standalone mode (localhost)
    // This ensures we make the call for all parent websites, including tcgservices.com
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!parentWebsite && !isLocalhost) return;

    let isMounted = true;
    setLoading(true);

    debugLog('Starting API calls', {
      employerId,
      planType: planTypeRaw,
      websiteName,
      isLocalhost
    });

    (async () => {
      try {
        // ----- 2) If we need to fetch relius_web from getEmployersByWebsite -----
        debugLog('Calling getEmployersByWebsite');
        const allEmployers = await employerSvc.getEmployersByWebsite({
          website: websiteName,
          page: "enrollSearch",
        });

        // find the one matching this employerId
        const matchedEmployer = allEmployers.find(
          (e) => String(e.employerIdForURL) === String(employerId)
        );

        // fallback if none found or missing relius_web
        const finalReliusWeb =
          matchedEmployer?.relius_web || "retirement.tcgservices.com";
          
        // console.log(finalReliusWeb)
        // console.log(matchedEmployer?.relius_web)

        // ----- 3) Now fetch the rest of the data from getEmployerDetails -----
        debugLog('Calling getEmployerDetails');
        const {
          employerInfo: apiEmployerInfo,
          approvedVendors,
          logo,
        } = await employerSvc.getEmployerDetails({
          employerIdForURL: employerId,
          page: "enrollSearch",
        });
        
        debugLog('API responses', {
          allEmployers: allEmployers?.length,
          approvedVendors: approvedVendors?.length,
          employerInfo: apiEmployerInfo
        });

        // Filter the returned vendors by planType if needed
        // If no planType is provided (deep link), show all vendors
        const filteredVendors = planTypeRaw 
          ? (approvedVendors || []).filter(
              (vendor) => {
                // Normalize both plan types for comparison
                const normalizedVendorPlanType = normalizePlanType(vendor.planType);
                const normalizedUrlPlanType = normalizePlanType(planTypeRaw);
                
                // Debug logging
                debugLog('Plan type comparison:', {
                  vendorPlanType: vendor.planType,
                  normalizedVendorPlanType,
                  urlPlanType: planTypeRaw,
                  normalizedUrlPlanType,
                  matches: normalizedVendorPlanType === normalizedUrlPlanType
                });
                
                return normalizedVendorPlanType === normalizedUrlPlanType;
              }
            )
          : (approvedVendors || []); // Show all vendors if no planType specified

        debugLog('Vendor filtering results:', {
          totalVendors: approvedVendors?.length || 0,
          filteredVendors: filteredVendors.length,
          planTypeRaw
        });

        // Check if this is an admin plan type and if there are no vendors
        const isAdminPlan = planTypeRaw && (planTypeRaw.includes("Admin") || planTypeRaw.includes("admin"));
        const hasNoVendors = filteredVendors.length === 0;
        
        debugLog('Admin plan check:', {
          isAdminPlan,
          hasNoVendors,
          planTypeRaw
        });

        // If it's an admin plan with no vendors, redirect to enrollment
        if (isAdminPlan && hasNoVendors) {
          debugLog('Redirecting to enrollment for admin plan with no vendors');
          // Redirect to the enrollment page for this employer
          const enrollPath = location.pathname.includes('/search-forms/') 
            ? `/search-forms/${employerId}`
            : `/search-enroll/${employerId}`;
          navigate(enrollPath, { replace: true });
          return; // Exit early since we're redirecting
        }

        if (isMounted) {
          setEmployerInfoData({
            name:
              apiEmployerInfo?.employer_name || apiEmployerInfo?.employerName || "",
            state: apiEmployerInfo?.state || "",
            reliusWeb: finalReliusWeb,
            logo: (logo && logo !== 'N/A')
              ? logo
              : 'https://tcg-public-files.s3.us-east-1.amazonaws.com/HUB-TCG%2BLogo-transparent.png'
          });
          setApprovedVendorsData(filteredVendors);
        }
      } catch (err) {
        console.error("Error fetching Investment Providers:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [employerId, planTypeRaw, websiteName, parentWebsite, debugLog, location.pathname, navigate]); // Include planTypeRaw directly, it can be empty string

  return (
    <ApprovedVendors
      loading={loading}
      employerId={employerId}
      planTypeRaw={planTypeRaw}
      planType={planTypeRaw}
      reliusWebFromState={employerInfoData.reliusWeb} // We'll pass this to the child
      approvedVendorsData={approvedVendorsData}
      employerInfoData={employerInfoData}
    />
  );
}
